(ns geir.business-logic
  "Business logic shared between frontend and backend."
  (:require #?(:clj  [geir-backend.util :as util]
               :cljs [ui.util           :as util])
            [clojure.edn    :as edn]
            [clojure.string :as str]
            [geir.constants :as const]))

(defn valid-numeric-id?
  "Given a string, e.g. like a user might enter into an ID filter input in the
   UI, returns true if we expect to be able to use it as a SQL query parameter
   on the backend.

   The intent is that we avoid attempting to use strings like \"not a number\"
   and \"1234123413241324123412342134\" as SQL parameters for an int column and
   causing an exception to be thrown.

   For context, see:
   https://app.shortcut.com/adzerk/story/22972/bad-input-in-id-filter-inputs-causes-the-error-log-monitor-to-go-off
   https://app.shortcut.com/adzerk/story/28730/non-numeric-ids-in-route-fragments-cause-the-error-log-monitor-to-go-off"
  [x]
  {:pre [(string? x)]}
  (when-let [number (parse-long x)]
    (<= 1 number const/SQL-INTEGER-MAX-VALUE)))

;; FIXME: We should really be asking Delivery what engine domain to use when we
;; present ad code to the user.
(defn default-engine-domain
  [network-id]
  (str "e-" network-id ".adzerk.net"))

(defn display-flight-name
  "Returns the name of a flight to be displayed in the UI, accounting for
   unnamed flights by using a default value."
  [flight-name]
  (if (seq flight-name)
    flight-name
    "Untitled flight"))

(defn adtype-wh
  [{:keys [width height name] :as _adtype}]
  (str width "x" height " - " name))

(defn audio?
  [{:keys [adformatid imagename] :as _creative}]
  (and (util/str= const/CREATIVE-FORMAT-FILE adformatid)
       (seq imagename)
       (re-find #"(\.mp3|\.wav)$" imagename)))

(defn image?
  "Checks if a string ends with a known image file extension"
  [{:keys [imagename] :as _creative}]
  (and (seq imagename)
       (re-find #"(\.gif|\.jpg|\.jpeg|\.png|\.svg|\.webp)$" imagename)))

(defn is-sso?
  [organization]
  (not (:iskevelidp organization)))

(defn is-nonkevel-sso?
  [organization]
  (and
   (not (:iskevelidp organization))
   (not (:isgoogleidp organization))))

(defn is-google-idp?
  [organization]
  (:isgoogleidp organization))

(def ^:const level->access-level-kw
  {"read" :access-level/read-only
   "amgr" :access-level/ad-manager
   "edit" :access-level/editor
   "admn" :access-level/admin
   ;; New auth flow uses "admin" instead of "admn"
   "admin" :access-level/admin})

;; Define a hierarchy where each access level is also, effectively, the one
;; below it. In other words, each access level has all of the permissions of the
;; level below it, and more.
(derive :access-level/admin :access-level/editor)
(derive :access-level/editor :access-level/ad-manager)
(derive :access-level/ad-manager :access-level/read-only)

(def ^:const level->org-access-level-kw
  {"read"       :org-access-level/read-only
   "admin"      :org-access-level/admin
   ;; FYI: This is not an official access level supported by the auth API, but it's 
   ;; used to indicate that the user has admin access to the organization but only 
   ;; read access to the data (i.e. omniscient users).
   "admin-read" :org-access-level/admin-read
   ;; nil if using former cognito auth flow
   nil          :org-access-level/not-set})

(derive :org-access-level/admin :org-access-level/admin-read)
(derive :org-access-level/admin-read :org-access-level/read-only)

(defn roas
  "Calculates ROAS based on GMV and Revenue.

   The basic formula is GMV / Revenue, but this function also ensures that we
   correctly handle GMV and Revenue being absent/nil or 0.

   Returns nil if ROAS cannot be calculated, e.g. if GMV and/or Revenue are
   missing."
  [gmv revenue]
  (when (and gmv revenue)
    (if (zero? revenue)
      0
      (/ gmv revenue))))

(defn ltvroas
  "Calculates LTVROAS based on LTV Sales and Revenue.

   The basic formula is LTV Sales / Revenue, but this function also ensures that we
   correctly handle LTV Sales and Revenue being absent/nil or 0.

   Returns nil if LTVROAS cannot be calculated, e.g. if LTV and/or Revenue are
   missing."
  [ltvsales revenue]
  (when (and ltvsales revenue)
    (if (zero? revenue)
      0
      (/ ltvsales revenue))))

(defn flight-automatically-tracks-conversions?
  "
  Given the number representation of a flight's goaltype and ratetype,
  returns whether or not the flight should automatically track conversions.
  "
  [{:keys [goaltype ratetype]}]
  (or (= const/CONVERSIONS goaltype)
      (contains? const/cpa-rate-types ratetype)))

(defn- process-scalar-type
  "Processes the scalar operators into kevelog based on the type used."
  [value]
  (let [type (get value "type")]
    (cond
      (= "!=" type) "not="
      :else type)))

(defn- process-array-value
  "Processes the value into kevelog based on the type used."
  [value]
  (cond
    (= "array-catalogsearchkeyref" (get value "type"))
    (mapv (fn [val] (get val "id")) (get value "value"))

    :else
    (get value "value")))

(defn- special-character-fix
  "Fix for special characters not retrieving search results.

   This was discussed with the systems team in a Slack thread [here](https://adzerk.slack.com/archives/C2K5DGXSQ/p1722616406369109)."
  [value]
  (str/replace value #"\s*([!*%_@^$&(}])\s*" "\\\\$1"))

(defn filters-to-kevelog-where
  "Processes filters formatted in doc/schema/filters/geir-filter.schema format and outputs them
   in a format to be used as the input for a kevelog 'where'."
  [filters itemdb-table]
  (if filters
    (let [to-kevelog (fn [reduction current]
                       (cond
                         (= "oneof" (get current "type"))
                         (concat
                          reduction
                          [[itemdb-table, ["one-of" (get current "key")] (process-array-value (get current "value"))]])

                         ;; FIXME: Special treatment for "matches" which supports single words
                         ;; only, so it's necessary to split each word into separate conditions.
                         ;; E.g. "music" AND "stand" for "music stand".
                         (= "matches" (get current "type"))
                         (concat
                          reduction
                          (map
                           (fn [v]
                             [itemdb-table ["matches" (get current "key")] v])
                           (-> (get-in current ["value" "value"])
                               special-character-fix
                               (str/split #" "))))

                         :else
                         (concat
                          reduction
                          [[itemdb-table
                            [(process-scalar-type current) (get current "key")]
                            (get-in
                             current
                             (cond
                               (= "catalogsearchkeyref" (get-in current ["value" "type"]))
                               ["value" "value" "id"]

                               :else
                               ["value" "value"]))]])))]
      (reduce to-kevelog [] filters))
    []))

(defn valid-goal-type?
  "Checks if the combination of a given goal type and selection algorithm is valid."
  [goal-type selection-algoritm]
  (let [goal-type (edn/read-string (str goal-type))]
    (cond
      (= const/AUCTION selection-algoritm)
      (contains? #{const/PERCENTAGE
                   const/REVENUE
                   const/DAILY-REVENUE
                   const/MONTHLY-REVENUE}
                 goal-type)
      (= const/AD-CHAIN-OPTIMIZED selection-algoritm)
      (= const/PERCENTAGE goal-type)
      :else
      true)))

(defn campaign-approval-status-allows-edit?
  "Checks if the provided `campaign` is editable.

   Campaign is not editable when:
   - Campaign is active
   - A request to activate the campaign is pending or approved"
  [campaign]
  (let [active?         (or (:isactive campaign)
                            (:IsActive campaign))
        approval-status (or (:approvalstatus campaign)
                            (:ApprovalStatus campaign))
        status
        (get approval-status :Status)]
    (not (or active?
             (some
              #(= status %)
              [const/APPROVAL_STATUS_ACTIVATE_REQUESTED
               const/APPROVAL_STATUS_ACTIVATE_APPROVED])))))

(defn campaign-approval-status-allows-activation?
  "Checks if the provided `campaign` is activatable.

   Campaign is activatable when:
   - Campaign is inactive
   - A request to activate the campaign is approved"
  [campaign]
  (let [active?         (or (:isactive campaign)
                            (:IsActive campaign))
        approval-status (or (:approvalstatus campaign)
                            (:ApprovalStatus campaign))
        status
        (get approval-status :Status)]
    (and (not active?)
         (= status const/APPROVAL_STATUS_ACTIVATE_APPROVED))))

(defn campaign-approval-status-allows-deactivation?
  "Checks if the provided `campaign` is activatable.

   Campaign is activatable when:
   - Campaign is inactive
   - A request to activate the campaign is approved"
  [campaign]
  (let [active?         (or (:isactive campaign)
                            (:IsActive campaign))
        approval-status (or (:approvalstatus campaign)
                            (:ApprovalStatus campaign))
        status
        (get approval-status :Status)]
    (and active?
         (= status const/APPROVAL_STATUS_DEACTIVATE_APPROVED))))
